import { useEffect } from "react";
import { Link } from "react-router-dom";
import { PRODUCT_MARKS } from "../brand/marks";
import { ALL_PRODUCTS, SOLUTIONS } from "../nav/navigation";
import type { PillarPageContent } from "../content/types";

/**
 * Category page template: positioning headline, the three-beat problem framing,
 * the products in the pillar, then related outcomes. Product detail stays on
 * the product pages so there is one place to change it.
 */
export function PillarPage({ content }: { content: PillarPageContent }) {
  useEffect(() => {
    document.title = `${content.eyebrow} — Odingard`;
  }, [content.eyebrow]);

  const products = content.products
    .map((slug) => ALL_PRODUCTS.find((p) => p.slug === slug))
    .filter((p): p is NonNullable<typeof p> => Boolean(p));

  const solutions = (content.solutions ?? [])
    .map((href) => SOLUTIONS.find((s) => s.href === href))
    .filter((s): s is NonNullable<typeof s> => Boolean(s));

  return (
    <main className="page pillar" id="main">
      <section className="pillar__hero">
        <p className="page__eyebrow">{content.eyebrow}</p>
        <h1>{content.headline}</h1>
        <p className="page__lede">{content.lede}</p>
      </section>

      <section className="pillar__problems">
        {content.problems.map((problem) => (
          <article key={problem.title}>
            <h2>{problem.title}</h2>
            <p>{problem.body}</p>
          </article>
        ))}
      </section>

      <section className="pillar__products">
        <h2>Products</h2>
        <div className="home__cards">
          {products.map((p) => {
            const Mark = PRODUCT_MARKS[p.mark];
            return (
              <Link key={p.slug} to={p.href} className="mm__card">
                <span className="mm__lockup" style={{ color: p.accent }}>
                  <Mark size={30} />
                  <b>{p.name}</b>
                </span>
                <span className="mm__descriptor">{p.descriptor}</span>
                <span className="mm__blurb">{p.blurb}</span>
              </Link>
            );
          })}
        </div>
      </section>

      {solutions.length > 0 && (
        <section className="pillar__solutions">
          <h2>Related outcomes</h2>
          <ul className="pdp__links">
            {solutions.map((s) => (
              <li key={s.href}>
                <Link to={s.href}>{s.name} →</Link>
              </li>
            ))}
          </ul>
        </section>
      )}

      <section className="pdp__close">
        <h2>{content.close.heading}</h2>
        <p className="pdp__body">{content.close.body}</p>
        <div className="pdp__ctas">
          {content.close.ctas.map((cta, i) =>
            cta.external ? (
              <a
                key={cta.href}
                className={i === 0 ? "btn btn--primary" : "btn btn--quiet"}
                href={cta.href}
              >
                {cta.label}
              </a>
            ) : (
              <Link
                key={cta.href}
                className={i === 0 ? "btn btn--primary" : "btn btn--quiet"}
                to={cta.href}
              >
                {cta.label}
              </Link>
            ),
          )}
        </div>
      </section>
    </main>
  );
}
